import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/store/useAuth';
import { renovarSessao } from '@/services/api';

/**
 * T5.3 — o access token vive em MEMÓRIA e morre em qualquer load completo
 * (F5, link <a>, aba nova). Quem devolve a sessão é o cookie HttpOnly de
 * refresh, mas ele só age se alguém o trocar por um access novo ANTES do
 * redirect para /login. Este gate é esse alguém: com identidade persistida e
 * memória vazia, tenta UMA renovação e só então decide a rota. Sem isto, todo
 * load completo expulsava o operador logado — o cookie válido ficava no
 * navegador sem nunca ser consultado.
 */
function useSessaoRestaurada(): boolean {
  const token = useAuth((s) => s.token);
  const username = useAuth((s) => s.username);
  const [restaurando, setRestaurando] = useState(() => !token && !!username);
  useEffect(() => {
    if (token || !username) {
      setRestaurando(false);
      return undefined;
    }
    let vivo = true;
    renovarSessao().finally(() => {
      if (vivo) setRestaurando(false);
    });
    return () => {
      vivo = false;
    };
    // Só no boot: depois disso quem renova é o interceptor de 401 do apiFetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return !restaurando;
}

/**
 * A-10 (perda 2) — o redirect para /login descartava o destino pretendido:
 * quem vinha de /planos → /billing?package=X caía no login e, depois dele,
 * na home. O caminho E a query string vão em `state.from`, no formato que o
 * react-router usa por convenção; a tela de login decide o que fazer com ele.
 */
function useDestinoPretendido(): string {
  const location = useLocation();
  return `${location.pathname}${location.search}${location.hash}`;
}

/**
 * Tela neutra do intervalo. Não é spinner de carregamento de dados: é o
 * instante entre "o navegador voltou" e "o servidor disse se a sessão vale".
 * Fica deliberadamente vazia e na cor do app — um flash de formulário de login
 * que some sozinho é pior que meio segundo de nada, porque ensina o operador a
 * duvidar se está ou não conectado.
 */
function RestaurandoSessao() {
  return (
    <div
      style={{ minHeight: '100vh', background: 'var(--bg-0)' }}
      role="status"
      aria-live="polite"
      aria-label="A restaurar a sessão"
    />
  );
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const pronto = useSessaoRestaurada();
  const token = useAuth((s) => s.token);
  const mustChange = useAuth((s) => s.mustChange);
  const from = useDestinoPretendido();
  if (!pronto) {
    return <RestaurandoSessao />;
  }
  if (!token) {
    return <Navigate to="/login" replace state={{ from }} />;
  }
  if (mustChange) {
    return <Navigate to="/trocar-senha" replace />;
  }
  return <>{children}</>;
}

export function RequireToken({ children }: { children: React.ReactNode }) {
  const pronto = useSessaoRestaurada();
  const token = useAuth((s) => s.token);
  const from = useDestinoPretendido();
  if (!pronto) {
    return <RestaurandoSessao />;
  }
  return token ? <>{children}</> : <Navigate to="/login" replace state={{ from }} />;
}
